import {
  Box,
  Chip,
  ClickAwayListener,
  Fab,
  Fade,
  Paper,
  Popper,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import React, { useContext, useEffect, useMemo, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { AuthContext } from "../../../contexts/AuthContext";
import {
  AbsoluteContainer,
  Image,
  RelativeContainer,
} from "../styled/Container.styled";
import { HiddenInput } from "../styled/Form.styled";
import { ConfiguratorContext } from "../../../contexts/ConfiguratorContext";
import { downloadFile } from "../../../data/configAPI";
import { PillButton } from "../styled/Button.styled";
import ImageGuidelinesTooltip from "./ImageGuidelinesTooltip";
import {
  ALLOWED_IMAGE_ACCEPT,
  ALLOWED_IMAGE_EXTENSIONS,
  ALLOWED_IMAGE_MEDIA_TYPES,
  BANNER_IMAGE_DASHBOARD_KEY,
  BANNER_IMAGE_PAYMENTS_KEY,
  BANNER_IMAGE_STATEMENTS_KEY,
  BANNER_URL_DASHBOARD_KEY,
  BANNER_URL_PAYMENTS_KEY,
  BANNER_URL_STATEMENTS_KEY,
} from "../../constants/Configurator.constants";
import { CLIENT_ADMIN_ROLE } from "../../constants/Roles.constants";
import {
  UPLOAD_IMAGE_SIZE_EXCEEDED,
  UPLOAD_IMAGE_TYPE_NOT_ALLOWED,
} from "../../constants/StatementsDocuments.constants";
import { isAllowedImageType } from "../../utils/utility-functions";
import ActionSnackbar from "../ActionSnackbar";

const BANNER_PAGES = [
  { imageKey: BANNER_IMAGE_DASHBOARD_KEY, urlKey: BANNER_URL_DASHBOARD_KEY },
  { imageKey: BANNER_IMAGE_PAYMENTS_KEY, urlKey: BANNER_URL_PAYMENTS_KEY },
  { imageKey: BANNER_IMAGE_STATEMENTS_KEY, urlKey: BANNER_URL_STATEMENTS_KEY },
];

const FALLBACK_BANNER_SRC = `${process.env.PUBLIC_URL || ""}/banner.jpg`;

const isBannerImageBlob = (data) =>
  data instanceof Blob &&
  data.size > 0 &&
  (!data.type ||
    data.type.includes("image") ||
    data.type === "application/octet-stream");

const fetchFallbackBannerBlob = async () => {
  try {
    const res = await fetch(FALLBACK_BANNER_SRC);
    if (res.ok) {
      return res.blob();
    }
  } catch (_) {
    /* ignore */
  }
  return null;
};

const BANNER_IMAGE_KEYS = [
  BANNER_IMAGE_DASHBOARD_KEY,
  BANNER_IMAGE_PAYMENTS_KEY,
  BANNER_IMAGE_STATEMENTS_KEY,
];

const downloadBannerWithFallbacks = async (
  token,
  primaryKey,
  impersonatedUser,
) => {
  const keysToTry = [
    primaryKey,
    ...BANNER_IMAGE_KEYS.filter((key) => key !== primaryKey),
  ];

  for (const key of keysToTry) {
    const imageData = await downloadFile(token, key, impersonatedUser);
    if (isBannerImageBlob(imageData)) {
      return imageData;
    }
  }

  return fetchFallbackBannerBlob();
};

const MarketingBanner = ({
  elementKey = "",
  hyperlinkElementKey = "",
  maxSizeKb = 100,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { token, userRoles, visibleRole, impersonatedUser } =
    useContext(AuthContext);
  const { edittedFields, addNewEdittedField, addBatchOfEdittedFields } =
    useContext(ConfiguratorContext);

  const [selectedFile, setSelectedFile] = useState(null);
  const [renderedImage, setRenderedImage] = useState();

  const [hyperlinkPopupOpen, setHyperlinkPopupOpen] = useState(false);
  const [anchorElement, setAnchorElement] = useState(null);
  const [imageURL, setImageURL] = useState("");
  const [formImageURL, setFormImageURL] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [copySuccessOpen, setCopySuccessOpen] = useState(false);

  const configBannerImage = useMemo(
    () => edittedFields.find((item) => item.key === elementKey)?.value,
    [edittedFields, elementKey],
  );

  const configBannerUrl = useMemo(
    () => edittedFields.find((item) => item.key === hyperlinkElementKey)?.value,
    [edittedFields, hyperlinkElementKey],
  );

  useEffect(() => {
    if (configBannerUrl != null && configBannerUrl !== "") {
      setImageURL(String(configBannerUrl));
      setFormImageURL(String(configBannerUrl));
    }
  }, [configBannerUrl]);

  useEffect(() => {
    if (isBannerImageBlob(configBannerImage)) {
      setSelectedFile(configBannerImage);
    }
  }, [configBannerImage]);

  useEffect(() => {
    let cancelled = false;

    const loadBannerImage = async () => {
      if (!token || !elementKey) {
        setSelectedFile(null);
        return;
      }

      if (isBannerImageBlob(configBannerImage)) {
        return;
      }

      const imageData = await downloadBannerWithFallbacks(
        token,
        elementKey,
        impersonatedUser,
      );

      if (cancelled || !isBannerImageBlob(imageData)) {
        return;
      }

      addNewEdittedField({
        key: elementKey,
        value: imageData,
        modified: false,
      });
      setSelectedFile(imageData);
    };

    loadBannerImage();

    return () => {
      cancelled = true;
    };
    // Intentionally omit configBannerImage — avoids aborting in-flight download when config updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, elementKey, impersonatedUser]);

  useEffect(() => {
    if (!isBannerImageBlob(selectedFile)) {
      setRenderedImage(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setRenderedImage(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const isClientAdmin =
    userRoles?.includes(CLIENT_ADMIN_ROLE) &&
    visibleRole === CLIENT_ADMIN_ROLE;

  const shouldShowBanner =
    Boolean(selectedFile) || isClientAdmin || Boolean(token);

  const uploadBanner = (event) => {
    const newFile = event?.target?.files?.[0];
    if (newFile) {
      // The upload is deferred until Publish, so without this the admin only
      // learns the type is unsupported once the whole batch is sent back with
      // a 400, by which point nothing says which image caused it.
      if (!isAllowedImageType(newFile, ALLOWED_IMAGE_MEDIA_TYPES)) {
        setSnackbarMessage(
          UPLOAD_IMAGE_TYPE_NOT_ALLOWED(ALLOWED_IMAGE_EXTENSIONS)
        );
        setSnackbarOpen(true);
        return;
      }
      // Binary KB, matching both what the operating system reports for the file
      // and the multipart cap tenant-service enforces. A decimal 1e6 limit
      // rejected files the admin saw as comfortably under 100KB.
      if (newFile.size <= maxSizeKb * 1024) {
        addNewEdittedField({
          key: elementKey,
          value: event.target.files[0],
          modified: true,
        });
        setSelectedFile(event?.target?.files?.[0]);
      } else {
        setSnackbarMessage(UPLOAD_IMAGE_SIZE_EXCEEDED(maxSizeKb));
        setSnackbarOpen(true);
      }
    }
  };

  const openHyperlinkForm = (e) => {
    setAnchorElement(e.currentTarget);
    setHyperlinkPopupOpen(true);
  };

  const handleHyperlinkFormClose = (e) => {
    if (anchorElement.current && anchorElement.current.contains(e.target)) {
      return;
    }

    setHyperlinkPopupOpen(false);
  };

  const submitImageHyperLinkForm = (e) => {
    if (e.key === "Enter") {
      addImageHyperlink();
    }
  };

  const addImageHyperlink = () => {
    setHyperlinkPopupOpen(false);
    addNewEdittedField({
      key: hyperlinkElementKey,
      value: formImageURL,
      modified: true,
    });
    setImageURL(formImageURL);
  };

  const bannerAction = () => {
    if (imageURL) {
      const nativeWindow = window.open(
        `${imageURL.substring(0, 4) !== "http" ? "http://" : ""}${imageURL}`,
        "_blank",
      );
      nativeWindow.focus();
    }
  };

  const copyToAllPages = () => {
    const otherPages = BANNER_PAGES.filter((p) => p.imageKey !== elementKey);
    const batch = [];
    otherPages.forEach(({ imageKey, urlKey }) => {
      if (selectedFile) {
        batch.push({ key: imageKey, value: selectedFile, modified: true });
      }
      batch.push({
        key: urlKey,
        value: imageURL || "",
        modified: true,
      });
    });
    if (batch.length > 0) {
      addBatchOfEdittedFields(batch);
      setCopySuccessOpen(true);
    }
  };

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <>
      <RelativeContainer
        width="100%"
        border={`1px solid ${theme.palette.text.hint}`}
        aspectratio={isMobile ? "3/1" : "10/2"}
        sx={{
          minHeight: isMobile ? "120px" : undefined,
          overflow: "hidden",
          background: theme.palette.background.paper,
        }}
      >
        {isBannerImageBlob(selectedFile) && renderedImage ? (
          <Image
            src={renderedImage}
            onClick={(e) => bannerAction()}
            sx={{
              position: "absolute",
              inset: 0,
              cursor: imageURL ? "pointer" : "default",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            alt="banner"
          />
        ) : isClientAdmin ? (
          <Image
            src="https://via.placeholder.com/1200x240/007bff/ffffff?text=Dashboard+Marketing+Banner"
            onClick={(e) => bannerAction()}
            sx={{
              position: "absolute",
              inset: 0,
              cursor: imageURL ? "pointer" : "default",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            alt="banner placeholder"
          />
        ) : (
          <Box sx={{ width: "100%", height: "100%" }} aria-hidden />
        )}
        {isClientAdmin ? (
          <>
            <AbsoluteContainer sx={{ 
              top: isMobile ? 4 : 5, 
              right: isMobile ? 4 : 5, 
              zIndex: 4 
            }}>
              <Tooltip title="Select Banner Image" placement="left">
                <label htmlFor="icon-button-file">
                  <HiddenInput
                    accept={ALLOWED_IMAGE_ACCEPT}
                    id="icon-button-file"
                    type="file"
                    onChange={uploadBanner}
                    value=""
                  />
                  <Fab
                    color="primary"
                    size={isMobile ? "small" : "small"}
                    sx={{ 
                      width: isMobile ? 24 : 30, 
                      height: isMobile ? 24 : 30, 
                      minHeight: isMobile ? 24 : 30 
                    }}
                    component="span"
                  >
                    <FileUploadRoundedIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
                  </Fab>
                </label>
              </Tooltip>
            </AbsoluteContainer>
            {selectedFile && (
              <>
                <AbsoluteContainer sx={{ 
                  top: isMobile ? 4 : 5, 
                  left: isMobile ? 4 : 5, 
                  zIndex: 4 
                }}>
                  <ImageGuidelinesTooltip ratio={isMobile ? "3:1" : "10:2"} placement="right" maxSize={`${maxSizeKb}KB`} />
                </AbsoluteContainer>
                <AbsoluteContainer sx={{ 
                  bottom: isMobile ? 2 : 5, 
                  right: isMobile ? 2 : 5, 
                  zIndex: 4 
                }}>
                  <Tooltip
                    title={imageURL ? "Edit Redirect URL" : "Add Redirect URL"}
                    placement="left"
                  >
                    <Chip
                      color={imageURL ? "success" : "error"}
                      size={isMobile ? "small" : "small"}
                      clickable
                      onClick={openHyperlinkForm}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: imageURL
                          ? theme.palette.success.light
                          : theme.palette.error.light,
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      }}
                      label={
                        imageURL ? (
                          <LinkIcon
                            fontSize={isMobile ? "small" : "small"}
                            sx={{ verticalAlign: "bottom" }}
                          />
                        ) : (
                          <LinkOffIcon
                            fontSize={isMobile ? "small" : "small"}
                            sx={{ verticalAlign: "bottom" }}
                          />
                        )
                      }
                    />
                  </Tooltip>
                  <Popper
                    open={hyperlinkPopupOpen}
                    anchorEl={anchorElement}
                    placement="top-end"
                    transition
                    sx={{
                      zIndex: 1300,
                      width: isMobile ? '250px' : 'auto',
                    }}
                  >
                    {({ TransitionProps }) => (
                      <Fade {...TransitionProps} timeout={350}>
                        <Paper sx={{ padding: isMobile ? 1 : 2 }}>
                          <ClickAwayListener
                            onClickAway={handleHyperlinkFormClose}
                          >
                            <Stack spacing={isMobile ? 1 : 2}>
                              <TextField
                                value={formImageURL}
                                onChange={(e) =>
                                  setFormImageURL(e.target.value)
                                }
                                onKeyUp={submitImageHyperLinkForm}
                                label="Link Address (URL)"
                                variant="standard"
                                size={isMobile ? "small" : "medium"}
                              />
                              <PillButton
                                variant="contained"
                                onClick={addImageHyperlink}
                                onKeyUp={submitImageHyperLinkForm}
                                sx={{
                                  fontSize: isMobile ? '0.875rem' : '1rem',
                                }}
                              >
                                Add
                              </PillButton>
                            </Stack>
                          </ClickAwayListener>
                        </Paper>
                      </Fade>
                    )}
                  </Popper>
                </AbsoluteContainer>
                <AbsoluteContainer sx={{ 
                  bottom: isMobile ? 2 : 5, 
                  left: isMobile ? 2 : 5, 
                  zIndex: 4 
                }}>
                  <Tooltip title="Copy this banner image and link to Dashboard, Payments, and Statements and Documents" placement="top">
                    <span>
                      <PillButton
                        size="small"
                        variant="outlined"
                        onClick={copyToAllPages}
                        startIcon={<ContentCopyIcon sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }} />}
                        sx={{
                          fontSize: isMobile ? '0.7rem' : '0.75rem',
                          fontWeight: 700,
                          py: 0.5,
                          px: 1,
                          backgroundColor: theme.palette.background.paper,
                          "&:hover": {
                            backgroundColor: theme.palette.background.paper,
                          },
                          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                        }}
                      >
                        Copy to all pages
                      </PillButton>
                    </span>
                  </Tooltip>
                </AbsoluteContainer>
              </>
            )}
          </>
        ) : (
          ""
        )}
      </RelativeContainer>
      <ActionSnackbar
        severity="error"
        borderColor={theme.palette.error.main}
        vertical="top"
        horizontal="center"
        width={isMobile ? "90%" : "50%"}
        isOpen={snackbarOpen}
        closeFn={() => {
          setSnackbarOpen(false);
        }}
        snackbarLabel={snackbarMessage}
        hasAction={false}
        autoHideDuration={6000}
      />
      <ActionSnackbar
        severity="success"
        borderColor={theme.palette.success.main}
        vertical="bottom"
        horizontal="center"
        width={isMobile ? "90%" : "40%"}
        isOpen={copySuccessOpen}
        closeFn={() => setCopySuccessOpen(false)}
        snackbarLabel="Image and link copied to all pages (Dashboard, Payments, Statements and Documents)"
        hasAction={false}
        autoHideDuration={4000}
      />
    </>
  );
};

export default MarketingBanner;